import React, { useMemo } from "react";
import { Dropdown, type DropdownOption } from "./dropdown";

export const CURATED_TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Kathmandu",
  "Asia/Dhaka",
  "Asia/Colombo",
  "Asia/Karachi",
  "Asia/Singapore",
  "Asia/Bangkok",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Riyadh",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Pacific/Auckland",
  "UTC",
] as const;

export function normalizeTimezoneId(tz?: string | null): string {
  if (!tz) return "Asia/Kolkata";
  const trimmed = tz.trim();
  if (trimmed === "Asia/Calcutta") return "Asia/Kolkata";
  return trimmed;
}

export function getAllSupportedTimezones(): string[] {
  const supported = new Set<string>(CURATED_TIMEZONES);
  if (typeof Intl !== "undefined" && typeof (Intl as any).supportedValuesOf === "function") {
    try {
      const list = (Intl as any).supportedValuesOf("timeZone") as string[];
      for (const tz of list) {
        if (tz === "Asia/Calcutta") {
          supported.add("Asia/Kolkata");
        } else {
          supported.add(tz);
        }
      }
    } catch {
      // ignore and use curated fallback
    }
  }
  supported.delete("Asia/Calcutta");
  return Array.from(supported);
}

export function getTimezoneOffsetMinutes(timeZone: string, date: Date = new Date()): number {
  try {
    const str = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    }).format(date);
    const match = str.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
    if (match) {
      const sign = match[1] === "-" ? -1 : 1;
      const hours = Number(match[2]);
      const minutes = Number(match[3] || 0);
      return sign * (hours * 60 + minutes);
    }
  } catch {
    // fallback
  }
  return 0;
}

export function formatTimezoneOffset(timeZone: string, date: Date = new Date()): string {
  try {
    const str = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    }).format(date);
    const match = str.match(/GMT([+-]\d{1,2}(?::\d{2})?)/);
    if (match) {
      return match[1];
    }
  } catch {
    // fallback
  }
  return "+00:00";
}

export function formatTimezoneLabel(timeZone: string): string {
  const offset = formatTimezoneOffset(timeZone);
  return `(UTC${offset}) ${timeZone}`;
}

export interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}

export function TimezoneSelect({
  value,
  onChange,
  ariaLabel = "Select Timezone",
  className,
  disabled,
}: TimezoneSelectProps) {
  const normalizedValue = normalizeTimezoneId(value);

  const options = useMemo(() => {
    const list = getAllSupportedTimezones();
    const mapped: DropdownOption<string>[] = list.map((tz) => ({
      value: tz,
      label: formatTimezoneLabel(tz),
    }));

    // Sort by UTC offset then alphabetical
    mapped.sort((a, b) => {
      const offA = getTimezoneOffsetMinutes(a.value);
      const offB = getTimezoneOffsetMinutes(b.value);
      if (offA !== offB) return offA - offB;
      return a.value.localeCompare(b.value);
    });

    // If current value is not in the list, ensure it's added
    if (normalizedValue && !mapped.some((o) => o.value === normalizedValue)) {
      mapped.unshift({
        value: normalizedValue,
        label: formatTimezoneLabel(normalizedValue),
      });
    }

    return mapped;
  }, [normalizedValue]);

  return (
    <Dropdown
      ariaLabel={ariaLabel}
      value={normalizedValue || "Asia/Kolkata"}
      options={options}
      onChange={(val) => onChange(normalizeTimezoneId(val))}
      className={className}
      searchable={true}
      disabled={disabled}
    />
  );
}
